import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';
import { setSidebarVisible } from 'redux/mode/actions';
import { RootReducer } from 'redux/reducers';
import TopBar from './TopBar';
import TokensView from './sections/TokensView';
import StylesView from './sections/StylesView';
import ComponentsView from './sections/ComponentsView';
import StoriesView from './sections/StoriesView';
import { TokenStylesInjector } from './sections/primitives/TokenStylesInjector';
import { ComponentScriptInjector } from './sections/components/ComponentScriptInjector';
import * as S from './styles';
import * as SectionS from './sections/styles';
import { shallowEqual } from 'react-redux';
import DesignSystemLeftPanel from '../Panel/DesignSystemLeftPanel';

const DesignSystemEditor = () => {
	const dispatch = useDispatch();
	const [searchParams, setSearchParams] = useSearchParams();

	const { sidebarVisible, designSystemContent, designSystemContentLoading } = useSelector(
		(r: RootReducer) => ({
			sidebarVisible: r.mode.sidebarVisible,
			designSystemContent: r.mode.designSystemContent,
			designSystemContentLoading: r.db.loading.activeDesignSystemVersion,
		}),
		shallowEqual
	);

	type SectionType = 'tokens' | 'styles' | 'components' | 'stories';
	const VALID_SECTIONS: SectionType[] = ['tokens', 'styles', 'components', 'stories'];

	const isValidSection = (value: string | null): value is SectionType => {
		return value !== null && VALID_SECTIONS.includes(value as SectionType);
	};

	const getSectionFromParams = (): SectionType => {
		const sectionParam = searchParams.get('section');
		return isValidSection(sectionParam) ? sectionParam : 'tokens';
	};

	const [activeSection, setActiveSection] = useState<SectionType>(getSectionFromParams());

	// Update active section when URL param changes
	useEffect(() => {
		const sectionParam = searchParams.get('section');
		if (isValidSection(sectionParam)) {
			setActiveSection(sectionParam);
		}
	}, [searchParams]);

	const handleSectionClick = (section: SectionType) => {
		setActiveSection(section);
		setSearchParams({ section });
	};

	const handleToggleSidebar = () => {
		dispatch(setSidebarVisible(!sidebarVisible));
	};

	const renderSectionView = () => {
		// Show loading state while DS content is being loaded
		if (designSystemContentLoading === 'loading') {
			return (
				<SectionS.SectionBase>
					<SectionS.SectionTitle>Loading Design System...</SectionS.SectionTitle>
					<SectionS.SectionContent>
						<p>Loading design system content...</p>
					</SectionS.SectionContent>
				</SectionS.SectionBase>
			);
		}

		// Show empty state if content hasn't loaded yet
		if (designSystemContentLoading === 'never-loaded' || !designSystemContent) {
			return (
				<SectionS.SectionBase>
					<SectionS.SectionTitle>Design System</SectionS.SectionTitle>
					<SectionS.SectionContent>
						<p>No design system content available.</p>
					</SectionS.SectionContent>
				</SectionS.SectionBase>
			);
		}

		switch (activeSection) {
			case 'tokens':
				return <TokensView />;
			case 'styles':
				return <StylesView />;
			case 'components':
				return <ComponentsView />;
			case 'stories':
				return <StoriesView />;
			default:
				return <TokensView />;
		}
	};

	return (
		<S.Container>
			<TokenStylesInjector />
			<ComponentScriptInjector />
			<S.TopBarWrapper>
				<TopBar
					activeSection={activeSection}
					onSectionClick={handleSectionClick}
					onToggleSidebar={handleToggleSidebar}
				/>
			</S.TopBarWrapper>
			<S.ContentWrapper>
				<S.LeftPanel>
					<DesignSystemLeftPanel />
				</S.LeftPanel>
				<S.MainContent>{renderSectionView()}</S.MainContent>
				<S.RightPanel>{/* Reserved for detail panel */}</S.RightPanel>
			</S.ContentWrapper>
		</S.Container>
	);
};

export default DesignSystemEditor;
